/**
 * @module utils/columnSort/shared/keys
 */

import type {
  IColumnSortItem,
  TBuilderType,
} from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';

/** 与 SortableType.Dimension / SortableType.Metric 一致，避免引入 configration 重依赖链 */
export const COLUMN_SORT_BUILDER_DIMENSION = 'columnDimensions' as TBuilderType;
export const COLUMN_SORT_BUILDER_METRIC = 'columnMetrics' as TBuilderType;

/** 与 UserFilter/FilterPanel/constant 一致，避免重依赖链 */
export const DATALAB_COMPARE_USER_FILTERS = 'DATALAB_COMPARE_USER_FILTERS';

/** 统一 builder_type（QB 快照为 dimension/metric，存储为 columnDimensions/columnMetrics） */
export function normalizeBuilderType(
  builderType: string | undefined,
): TBuilderType {
  if (
    builderType === COLUMN_SORT_BUILDER_DIMENSION ||
    builderType === 'dimension' ||
    builderType === 'columnDimensions'
  ) {
    return COLUMN_SORT_BUILDER_DIMENSION;
  }
  return COLUMN_SORT_BUILDER_METRIC;
}

export function normalizeColumnSortItem(item: IColumnSortItem): IColumnSortItem {
  return {
    ...item,
    table_code: item.table_code || 't0',
    builder_type: normalizeBuilderType(item.builder_type),
  };
}

export function isColumnSortDimension(item: IColumnSortItem): boolean {
  return normalizeBuilderType(item.builder_type) === COLUMN_SORT_BUILDER_DIMENSION;
}

export function dedupeColumnSortList(list: IColumnSortItem[]): IColumnSortItem[] {
  const seen = new Set<string>();
  const out: IColumnSortItem[] = [];
  for (const raw of list) {
    const item = normalizeColumnSortItem(raw);
    const id = getColumnSortItemId(item);
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(item);
  }
  return out;
}

/**
 * 面板「字段列排序」已写入 draft.column_sort（未 Confirm）。
 */
export function hasPanelDraftColumnSort(ctx: {
  draft: Record<string, unknown>;
}): boolean {
  const draftSort = ctx.draft.column_sort;
  return (
    'column_sort' in ctx.draft &&
    Array.isArray(draftSort) &&
    draftSort.length > 0
  );
}

export function getBaseColumnSortList(ctx: {
  base: Record<string, unknown>;
}): IColumnSortItem[] {
  const baseSort = ctx.base.column_sort;
  if (!Array.isArray(baseSort)) {
    return [];
  }
  return dedupeColumnSortList(baseSort as IColumnSortItem[]);
}

/** draft 列序相对 base 有未 Confirm 的面板拖拽改动 */
export function hasUnsavedPanelColumnSortOrder(ctx: {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
}): boolean {
  if (!hasPanelDraftColumnSort(ctx)) {
    return false;
  }
  const draft = ctx.draft.column_sort as IColumnSortItem[];
  return !isColumnSortOrderEqual(getBaseColumnSortList(ctx), draft);
}

/**
 * linkage 产出若恰好等于 base 列序，而面板 draft 仍与 base 不同，
 * 说明 QB 未产生新顺序，属于 stale pipeline 误把面板 draft 拉回 base。
 * QB 真实重排时 realign/reconcile 产出应 ≠ base，不会命中本判断。
 */
export function isStalePanelColumnSortRevert(
  ctx: {
    base: Record<string, unknown>;
    draft: Record<string, unknown>;
  },
  next: IColumnSortItem[],
): boolean {
  return (
    hasUnsavedPanelColumnSortOrder(ctx) &&
    isColumnSortOrderEqual(next, getBaseColumnSortList(ctx))
  );
}

/** 两列序是否包含相同列身份集合（忽略顺序） */
export function isColumnSortSameItemSet(
  a: IColumnSortItem[],
  b: IColumnSortItem[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const toSortedIds = (list: IColumnSortItem[]): string[] =>
    list
      .map(item => getColumnSortItemId(normalizeColumnSortItem(item)))
      .sort();
  const idsA = toSortedIds(a);
  const idsB = toSortedIds(b);
  return idsA.every((id, i) => id === idsB[i]);
}

/** 仅当 base/draft 存在非空 column_sort 数组时视为已持久化 */
export function hasPersistedColumnSort(ctx: {
  base: Record<string, unknown>;
  draft: Record<string, unknown>;
}): boolean {
  const draftSort = ctx.draft.column_sort;
  if (
    'column_sort' in ctx.draft &&
    Array.isArray(draftSort) &&
    draftSort.length > 0
  ) {
    return true;
  }
  const baseSort = ctx.base.column_sort;
  return Array.isArray(baseSort) && baseSort.length > 0;
}

/** 与旧 fieldColumnSort 一致：`% ` + 指标名 */
export const PERCENTAGE_PREFIX = '% ';

export function stripPercentagePrefix(value?: string): string {
  if (!value) {
    return '';
  }
  if (value.startsWith(PERCENTAGE_PREFIX)) {
    return value.slice(PERCENTAGE_PREFIX.length);
  }
  if (value.startsWith('%')) {
    return value.slice(1);
  }
  return value;
}

export function getColumnSortKey(
  item:
    | IColumnSortItem
    | {
        table_code?: string;
        table_id?: string;
        column?: string;
        column_name?: string;
        builder_type?: string;
      },
): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const tableCode = item.table_code || 't0';
  const tableId = item.table_id || '';
  let column = '';
  if ('column' in item && typeof item.column === 'string' && item.column) {
    column = item.column;
  } else if (
    'column_name' in item &&
    typeof item.column_name === 'string' &&
    item.column_name
  ) {
    column = item.column_name;
  }
  let builderType = '';
  if ('builder_type' in item && typeof item.builder_type === 'string') {
    builderType = normalizeBuilderType(item.builder_type);
  }
  return `${tableCode}_${tableId}_${column}_${builderType}`;
}

export function getColumnSortItemId(item: IColumnSortItem): string {
  return getColumnSortKey(item);
}

/**
 * 比较两条 column_sort 是否完全相同（按 getColumnSortItemId 逐位比较）。
 * QB 仅重排时用于判断是否写 patch。
 */
export function isColumnSortOrderEqual(
  a: IColumnSortItem[],
  b: IColumnSortItem[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (getColumnSortItemId(a[i]) !== getColumnSortItemId(b[i])) {
      return false;
    }
  }
  return true;
}

/** sanitize reconcile 后逐项比较：列身份 + 对比时间标记 + 展示名 */
function columnSortItemSanitizeEqual(
  a: IColumnSortItem,
  b: IColumnSortItem,
): boolean {
  const left = normalizeColumnSortItem(a);
  const right = normalizeColumnSortItem(b);
  return (
    getColumnSortItemId(left) === getColumnSortItemId(right) &&
    !!left.is_compare_date === !!right.is_compare_date &&
    String(left.column_display_cn ?? '') ===
      String(right.column_display_cn ?? '') &&
    String(left.column_display_en ?? '') ===
      String(right.column_display_en ?? '')
  );
}

/**
 * sanitize handler 写 patch 前的等价判断。
 *
 * 旧 early-exit 仅比较 column / table_code，会漏掉「对比时间关闭后展示名还原」等
 * 仅展示层变化，导致 base.column_sort 长期保留「对比时间」。
 */
export function isColumnSortSanitizeOutputEqual(
  current: IColumnSortItem[],
  next: IColumnSortItem[],
): boolean {
  if (current.length !== next.length) {
    return false;
  }
  for (let i = 0; i < current.length; i += 1) {
    if (!columnSortItemSanitizeEqual(current[i], next[i])) {
      return false;
    }
  }
  return true;
}

export function percentVirtualColumn(metric: string): string {
  return `${PERCENTAGE_PREFIX}${metric}`;
}

export function isCompareUserFilterColumn(column: string): boolean {
  return column === DATALAB_COMPARE_USER_FILTERS;
}

/**
 * 将 column_sort 项映射为表格列 code（与后端 columnName.name / AliTable code 对齐）。
 *
 * - 普通字段：`${table_code}_${column_display_cn}`
 * - 对比时间：`${table_code}_${column}`（后端 name 使用物理列名，非「对比时间」展示名）
 * - 对照组：`DATALAB_COMPARE_USER_FILTERS` 或 `${table_code}_${DATALAB_COMPARE_USER_FILTERS}`
 */
export function buildTableColumnOrderCodeFromSortItem(
  item: IColumnSortItem,
): string | null {
  if (!item?.column) {
    return null;
  }

  if (isCompareUserFilterColumn(item.column)) {
    const tableCode = item.table_code;
    return tableCode
      ? `${tableCode}_${DATALAB_COMPARE_USER_FILTERS}`
      : DATALAB_COMPARE_USER_FILTERS;
  }

  let namePart = String(item.column_display_cn ?? '').trim();
  if (item.is_compare_date) {
    namePart = item.column;
  }
  if (!namePart) {
    return null;
  }

  const tableCode = item.table_code;
  return tableCode ? `${tableCode}_${namePart}` : namePart;
}

/** column_sort 列表 → applyColumnOrder 所需的 orderCodes */
export function buildColumnSortOrderCodes(
  columnSort: IColumnSortItem[],
): string[] {
  return columnSort
    .map(buildTableColumnOrderCodeFromSortItem)
    .filter((code): code is string => Boolean(code));
}

export function matchFieldToSortItem(
  raw: Record<string, unknown>,
  item: IColumnSortItem,
): boolean {
  const column = String(raw.column_name ?? raw.column ?? '');
  const tableCode = String(raw.table_code ?? 't0');
  const tableId = String(raw.table_id ?? '');
  return (
    item.column === column &&
    (item.table_code || 't0') === tableCode &&
    String(item.table_id ?? '') === tableId
  );
}
